// Swiss compliance (nDSG/FINMA) and breach monitoring API
import { api } from './client';

// ============================================================================
// SWISS COMPLIANCE TYPES
// ============================================================================

export interface SwissComplianceControl {
  id: string;
  name: string;
  name_de: string;
  description: string;
  description_de: string;
  category: string;
  category_de: string;
  article?: string;
  weight: number;
  vuln_matches: string[];
}

export interface SwissComplianceResult {
  control: SwissComplianceControl;
  status: 'compliant' | 'partial' | 'non_compliant' | 'not_assessed' | 'not_assessable';
  score: number;
  affecting_findings: any[];
  remediation_priority: 'critical' | 'high' | 'medium' | 'low';
  notes: string;
}

export interface SwissComplianceCategory {
  name: string;
  name_de: string;
  score: number;
  status: string;
  count: number;
  passed: number;
}

export interface SwissComplianceReport {
  framework: 'ndsg' | 'finma';
  framework_name: string;
  framework_name_de: string;
  generated_at: string;
  overall_score: number;
  overall_status: string;
  results: SwissComplianceResult[];
  severity_summary: Record<string, number>;
  control_categories: SwissComplianceCategory[];
}

export interface ComplianceFramework {
  id: string;
  name: string;
  name_de: string;
  description: string;
  description_de: string;
  controls_count: number;
}

// ============================================================================
// SWISS COMPLIANCE API
// ============================================================================

export const swissComplianceAPI = {
  // Get available compliance frameworks
  getFrameworks: () => {
    console.log('📋 Fetching available Swiss compliance frameworks');
    return api.get<ComplianceFramework[]>('/compliance/frameworks');
  },

  // Get nDSG (Swiss Data Protection Act) compliance report for a scan
  getNDSGCompliance: (scanId: number) => {
    console.log('🇨🇭 Fetching nDSG compliance for scan:', scanId);
    return api.get<SwissComplianceReport>(`/scans/${scanId}/compliance/ndsg`, {
      timeout: 120000 // 2 minutes timeout for large compliance reports
    });
  },

  // Get FINMA (Financial Market Supervisory Authority) compliance report for a scan
  getFINMACompliance: (scanId: number) => {
    console.log('🏦 Fetching FINMA compliance for scan:', scanId);
    return api.get<SwissComplianceReport>(`/scans/${scanId}/compliance/finma`, {
      timeout: 120000 // 2 minutes timeout for large compliance reports
    });
  },

  // Export compliance PDF report
  exportCompliancePDF: (scanId: number, framework: 'ndsg' | 'finma', language: string = 'en') => {
    console.log('📄 Exporting compliance PDF:', framework, 'for scan:', scanId, 'language:', language);
    return api.get(`/scans/${scanId}/export/pdf-swiss-compliance?framework=${framework}&language=${language}`, {
      responseType: 'blob',
      timeout: 180000 // 3 minutes timeout for PDF generation
    });
  },
};

// ============================================================================
// BREACH MONITORING TYPES
// ============================================================================

export interface BreachMonitoredDomain {
  id: number;
  project_id: number;
  client_id?: number;
  domain: string;
  is_active: boolean;
  monitoring_frequency: 'daily' | 'weekly' | 'monthly';
  last_checked_at?: string;
  next_check_at?: string;
  total_findings: number;
  high_findings: number;
  new_findings_count: number;
  created_at: string;
  updated_at: string;
}

export interface BreachResult {
  id: number;
  domain_id: number;
  project_id: number;
  client_id?: number;
  dehashed_id: string;
  email: string;
  name: string;
  username: string;
  phone: string;
  address: string;
  date_of_birth: string;
  ip_address: string;
  password_masked: string;
  has_password: boolean;
  has_hash: boolean;
  hash_type: string;
  database_name: string;
  breach_date?: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  is_new: boolean;
  acknowledged_at?: string;
  acknowledged_by?: number;
  ticket_id?: number;
  created_at: string;
  updated_at: string;
}

export interface BreachMonitoringStatus {
  feature_enabled: boolean;
  is_active: boolean;
  max_domains: number;
  monitored_domains: number;
  client_domains_used: number;
  active_domains: number;
  total_findings: number;
  new_findings: number;
  monthly_quota: number;
  used_quota: number;
  scheduling_enabled: boolean;
  next_scheduled_scan: string | null;
  last_scan_time: string | null;
  can_reveal_credentials?: boolean;
}

export interface BreachCredentialReveal {
  available: boolean;
  password: string;
  hash: string;
  hash_type: string;
  has_password: boolean;
  has_hash: boolean;
}

export interface BreachScanLog {
  id: number;
  domain_id: number;
  project_id: number;
  status: 'success' | 'failed' | 'partial';
  total_found: number;
  new_findings: number;
  high_findings: number;
  api_credits_used: number;
  error_message?: string;
  duration_ms: number;
  created_at: string;
}

export interface BreachStatistics {
  severity_counts: { severity: string; count: number }[];
  top_databases: { database_name: string; count: number }[];
  unique_emails: number;
  recent_findings: number;
}

// Breach monitoring evolution types
export interface BreachMonthlyStats {
  id: number;
  domain_id: number;
  project_id: number;
  client_id?: number;
  year_month: number;
  total_findings: number;
  new_findings: number;
  critical_findings: number;
  high_findings: number;
  medium_findings: number;
  low_findings: number;
  info_findings: number;
  unique_emails: number;
  passwords_exposed: number;
  hashes_exposed: number;
  acknowledged_count: number;
  tickets_created: number;
  scans_performed: number;
  api_credits_used: number;
  created_at: string;
  updated_at: string;
}

export interface BreachProjectStats {
  id: number;
  project_id: number;
  client_id?: number;
  year_month: number;
  total_domains: number;
  active_domains: number;
  total_findings: number;
  new_findings: number;
  critical_findings: number;
  high_findings: number;
  unique_emails: number;
  passwords_exposed: number;
  acknowledged_count: number;
  tickets_created: number;
  scans_performed: number;
  api_credits_used: number;
  created_at: string;
  updated_at: string;
}

export interface BreachEvolutionData {
  months: BreachProjectStats[];
}

export interface BreachDomainEvolutionData {
  months: BreachMonthlyStats[];
}

export interface BreachEvolutionSummary {
  current_month: {
    year_month: number;
    total_findings: number;
    new_findings: number;
    critical_findings: number;
    high_findings: number;
    unique_emails: number;
    passwords_exposed: number;
  };
  last_month: {
    year_month: number;
    total_findings: number;
    new_findings: number;
    critical_findings: number;
    high_findings: number;
    unique_emails: number;
    passwords_exposed: number;
  };
  trends: {
    total_findings_change: number;
    new_findings_change: number;
    critical_change: number;
    high_change: number;
    emails_change: number;
    passwords_change: number;
  };
}

// ============================================================================
// BREACH MONITORING API
// ============================================================================

export const breachMonitoringAPI = {
  // Get breach monitoring status for a project
  getStatus: (projectId: number) => {
    return api.get<BreachMonitoringStatus>(`/breach-monitoring/project/${projectId}/status`);
  },

  // Get monitored domains for a project
  getDomains: (projectId: number) => {
    return api.get<BreachMonitoredDomain[]>(`/breach-monitoring/project/${projectId}/domains`);
  },

  // Add a domain to monitor
  addDomain: (projectId: number, data: { domain: string; is_active?: boolean; monitoring_frequency?: string }) => {
    return api.post<BreachMonitoredDomain>(`/breach-monitoring/project/${projectId}/domains`, data);
  },

  // Update a monitored domain
  updateDomain: (domainId: number, data: { is_active?: boolean; monitoring_frequency?: string }) => {
    return api.put<BreachMonitoredDomain>(`/breach-monitoring/domains/${domainId}`, data);
  },

  // Delete a monitored domain
  deleteDomain: (domainId: number) => {
    return api.delete(`/breach-monitoring/domains/${domainId}`);
  },

  // Scan a domain for breaches
  scanDomain: (domainId: number) => {
    return api.post<{ status: string; total_found: number; new_findings: number; high_findings: number; duration_ms: number; next_check: string }>(`/breach-monitoring/domains/${domainId}/scan`);
  },

  // Get breach results for a project
  getResults: (projectId: number, params?: { domain_id?: number; severity?: string; new_only?: boolean; database_name?: string }) => {
    return api.get<BreachResult[]>(`/breach-monitoring/project/${projectId}/results`, { params });
  },

  // Acknowledge a single result
  acknowledgeResult: (resultId: number) => {
    return api.post<BreachResult>(`/breach-monitoring/results/${resultId}/acknowledge`);
  },

  // Reveal the cleartext credentials for a single breach result (authorized + audited)
  revealCredentials: (resultId: number) => {
    return api.get<BreachCredentialReveal>(`/breach-monitoring/results/${resultId}/credentials`);
  },

  // Bulk acknowledge results
  bulkAcknowledge: (projectId: number, data: { result_ids?: number[]; all?: boolean }) => {
    return api.post(`/breach-monitoring/project/${projectId}/acknowledge`, data);
  },

  // Get scan logs for a project
  getLogs: (projectId: number) => {
    return api.get<BreachScanLog[]>(`/breach-monitoring/project/${projectId}/logs`);
  },

  // Get breach statistics for a project
  getStatistics: (projectId: number) => {
    return api.get<BreachStatistics>(`/breach-monitoring/project/${projectId}/statistics`);
  },

  // Get API balance (admin)
  getAPIBalance: () => {
    return api.get<{ balance: number }>(`/breach-monitoring/api-balance`);
  },

  // Evolution/Historical statistics endpoints
  getEvolution: (projectId: number, params?: { months?: number }) => {
    return api.get<BreachEvolutionData>(`/breach-monitoring/project/${projectId}/evolution`, { params });
  },

  getEvolutionSummary: (projectId: number) => {
    return api.get<BreachEvolutionSummary>(`/breach-monitoring/project/${projectId}/evolution/summary`);
  },

  getDomainEvolution: (domainId: number, params?: { months?: number }) => {
    return api.get<BreachDomainEvolutionData>(`/breach-monitoring/domains/${domainId}/evolution`, { params });
  },

  // Admin-only manual scan triggers
  triggerManualScan: (domainId: number) => {
    return api.post<{ status: string; message: string }>(`/admin/breach-monitoring/domains/${domainId}/manual-scan`);
  },

  triggerProjectScan: (projectId: number) => {
    return api.post<{ status: string; message: string; domains_scanned: number }>(`/admin/breach-monitoring/project/${projectId}/manual-scan`);
  },

  // Export breach report as HTML
  exportBreachReportHTML: (projectId: number) => {
    return api.get(`/breach-monitoring/project/${projectId}/export/html`, {
      responseType: 'blob',
      timeout: 60000,
    });
  },
};
